import { NodeOf } from "@p0security/shared/graph/types";
import {
  ALL_SCOPE_SENTINEL,
  ProviderOrAll,
} from "@p0security/shared/types/assessment";
import { useObjectMemo } from "hooks/useObjectMemo";
import { createContext, useCallback, useState } from "react";

import { GraphVisualizationInstance } from "../components/GraphVisualization";

/**
 * RendererParams and CustomNodeRendererOptions are parameters that can affect how nodes get rendered,
 * but at a more global scope (i.e. whether the particular graph being shown is an inventory graph,
 * a posture graph, or a findings drawer graph)
 */
export type CustomNodeRendererOptions = {
  detailed?: boolean;
  hideThis?: boolean;
  noHover?: boolean;
};

export type DiscoverVisualizationContext = {
  provider: ProviderOrAll;
  open: Record<string, boolean>;
  toggleOpen: (key: string) => void;
  instance?: GraphVisualizationInstance<any>;
  setInstance: React.Dispatch<
    React.SetStateAction<GraphVisualizationInstance<any> | undefined>
  >;
};

/**
 * GraphContext is a React context that holds the parameters that affect how nodes get rendered.
 * It also can contain an instance of the GraphVisualization component, which can be used to
 * interact with the graph visualization imperatively. This context is used by custom nodes
 */
export const DiscoverVisualizationContext =
  createContext<DiscoverVisualizationContext>({
    provider: ALL_SCOPE_SENTINEL,
    open: {},
    toggleOpen: () => {},
    setInstance: () => {},
  });

export const DiscoverVisualizationProvider: React.FC<
  React.PropsWithChildren & { provider: ProviderOrAll }
> = ({ children, provider }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [instance, setInstance] = useState<{
    selectNode: <A extends object>(node: NodeOf<A> | undefined) => void;
  }>();
  const toggleOpen = useCallback((key: string) => {
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const value = useObjectMemo({
    provider,
    open,
    toggleOpen,
    instance,
    setInstance,
  });

  return (
    <DiscoverVisualizationContext.Provider value={value}>
      {children}
    </DiscoverVisualizationContext.Provider>
  );
};
